import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  Alert,
  Paper,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { ROLE_FONT_FAMILY, roleModuleRootSx, roleTypography, SESSION_USER_ID_KEY } from "../../Util/VMConstants";
import StorageFactory from "../../Util/StorageFactory";
import {
  API_ADD_ROLES,
  API_DELETE_ROLE,
  API_GET_ALL_PERMISSIONS,
  API_GET_ROLES,
  API_GET_ROLE_BY_NAME,
  API_UPDATES_ROLE,
} from "../../Util/URLConstants";
import {
  fetchDefaultRolePermissionCount,
  resolveRecommendedRolePermissions,
} from "../../Services/roleService";
import {
  align_center,
  align_left,
  dataGridStyle,
  VM_Colors,
  vmContainedButtonSx,
} from "../../Styles/VMStyles";
import StyledInputField from "../../Components/Custom/StyledInputField";
import { toast } from "react-toastify";
import TitleAndCloseComponent from "../../Components/TitleAndCloseComponent";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import { renderSNo } from "../../Util/UtilHelper";
import ReusableDataGrid from "../../Components/ReusableDataGrid";
import TitleWithAddButton from "../Home/TitleWithAddButton";
import Loader from "../../Components/Loader";
import {
  countTemplatePermissions,
  getRecommendedRoleByName,
  isRecommendedRoleName,
  RECOMMENDED_ROLES,
  resolveTemplatePermissionIds,
  ROLE_MODE_CUSTOM,
  ROLE_MODE_RECOMMENDED,
} from "../../Util/roleTemplatesHelper";

const RECOMMENDED_ROLE_ICONS = {
  Manager: SupervisorAccountIcon,
  Executive: BusinessCenterIcon,
  Receptionist: SupportAgentIcon,
  "Security Guard": SecurityIcon,
};

const MAX_VISIBLE_PERMISSIONS = 3;

const primaryActionButtonSx = {
  ...vmContainedButtonSx,
  fontFamily: ROLE_FONT_FAMILY,
  fontSize: 14,
  fontWeight: 600,
  borderRadius: 2,
  px: 3,
  py: 1,
  minHeight: 40,
};

const secondaryActionButtonSx = {
  flex: 1,
  borderRadius: 2,
  textTransform: "none",
  fontFamily: ROLE_FONT_FAMILY,
  fontSize: 14,
  fontWeight: 600,
  color: VM_Colors.primary,
  borderColor: VM_Colors.primary,
  px: 3,
  py: 1,
  minHeight: 40,
  "&:hover": {
    borderColor: VM_Colors.primary,
    backgroundColor: `${VM_Colors.primary}08`,
  },
};

const compactRoleFieldSx = {
  backgroundColor: VM_Colors.white,
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: "8px",
    fontSize: 14,
    fontFamily: ROLE_FONT_FAMILY,
    backgroundColor: VM_Colors.white,
    "& fieldset": { borderColor: "#C3C3C3" },
    "&:hover fieldset": { borderColor: "#A0A0A0" },
    "&.Mui-focused fieldset": { borderColor: VM_Colors.primary },
  },
  "& .MuiInputBase-input": {
    py: "8.5px",
    fontSize: 14,
    fontFamily: ROLE_FONT_FAMILY,
  },
};

const roleSelectionPalette = {
  bg: VM_Colors.white1,
  bgHover: "#DCE6F7",
  border: `${VM_Colors.primary}33`,
  accent: VM_Colors.primary,
  textColor: VM_Colors.primary,
  iconBg: `${VM_Colors.primary}18`,
  iconColor: VM_Colors.primary,
  selectedBg: VM_Colors.primary,
  selectedHoverBg: "#162e66",
  selectedShadow: "0 6px 18px rgba(30, 58, 138, 0.28)",
  hoverShadow: "rgba(30, 58, 138, 0.12)",
};

const statCardSx = {
  p: 2,
  borderRadius: 2,
  border: `1px solid ${VM_Colors.light_gray}`,
  backgroundColor: VM_Colors.white,
  height: "100%",
};

function VMRoles() {
  const [roles, setRoles] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [permissionsList, setPermissionsList] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [roleMode, setRoleMode] = useState(ROLE_MODE_RECOMMENDED);
  const [selectedRecommendedRole, setSelectedRecommendedRole] = useState(null);
  const [customRoleName, setCustomRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [permissionSource, setPermissionSource] = useState(null);
  const [pendingTemplateRole, setPendingTemplateRole] = useState(null);
  const [defaultRoleCounts, setDefaultRoleCounts] = useState({});
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permissionsPreview, setPermissionsPreview] = useState(null);
  const [allPermissionsCatalog, setAllPermissionsCatalog] = useState([]);
  const [roleExistsDialog, setRoleExistsDialog] = useState(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Check if screen is small

  const sessionFactory = new StorageFactory("session");
  const user_id = sessionFactory.get(SESSION_USER_ID_KEY);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_GET_ROLES + "?user_id=" + user_id);
      const rowsWithId = res.data.roles.map((role, index) => ({
        id: role.id || index,
        s_no: index + 1,
        name: role.role_name,
        description: role.description,
        permissions: role.permissions,
      }));
      setRoles(rowsWithId);
    } catch (err) {
      console.error("Error fetching roles:", err);
    } finally {
      setLoading(false);
    }
  }, [user_id]);


  const applyTemplatePermissions = useCallback(
    (recommendedRole) => {
      if (!recommendedRole || !permissionsList.length) {
        setPendingTemplateRole(recommendedRole);
        return;
      }

      setSelectedPermissions(
        resolveTemplatePermissionIds(permissionsList, recommendedRole.template)
      );
      setPermissionSource("template");
      setPendingTemplateRole(null);
    },
    [permissionsList]
  );

  const loadDefaultRoleCounts = useCallback(async () => {
    const counts = {};

    await Promise.all(
      RECOMMENDED_ROLES.map(async (recommendedRole) => {
        const apiCount = await fetchDefaultRolePermissionCount(recommendedRole.name);
        counts[recommendedRole.id] =
          apiCount ||
          countTemplatePermissions(permissionsList, recommendedRole.template);
      })
    );

    setDefaultRoleCounts(counts);
  }, [permissionsList]);

  const applyRecommendedRole = useCallback(
    async (recommendedRole) => {
      if (!recommendedRole) return;

      setSelectedRecommendedRole(recommendedRole);
      setRoleDescription(recommendedRole.description);
      setErrors((prev) => ({ ...prev, recommendedRole: undefined, roleMode: undefined }));

      try {
        setLoading(true);
        const resolved = await resolveRecommendedRolePermissions(
          recommendedRole.name,
          user_id
        );

        if (resolved?.permissionIds?.length) {
          setSelectedPermissions(resolved.permissionIds);
          setRoleDescription(resolved.description || recommendedRole.description);
          setPermissionSource(resolved.source);
          setPendingTemplateRole(null);
          return;
        }
      } catch (err) {
        console.error("Error resolving recommended role permissions:", err);
      } finally {
        setLoading(false);
      }

      applyTemplatePermissions(recommendedRole);
    },
    [applyTemplatePermissions, user_id]
  );


  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_GET_ALL_PERMISSIONS);
      const permissions = res.data.permissions || [];
      setPermissionsList(permissions);
      return permissions;
    } catch (err) {
      console.error("Error fetching permissions:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const resetRoleForm = () => {
    setRoleMode(ROLE_MODE_RECOMMENDED);
    setSelectedRecommendedRole(null);
    setCustomRoleName("");
    setRoleDescription("");
    setSelectedPermissions([]);
    setPermissionSource(null);
    setPendingTemplateRole(null);
    setErrors({});
    setSelectAll(false);
  };

  const handleAddRoleClick = async () => {
    setEditId(null);
    resetRoleForm();
    setDrawerOpen(true);
    await fetchPermissions();
  };

  const handleEditClick = (role) => {
    setEditId(role.id);
    const recommendedRole = getRecommendedRoleByName(role.name);

    if (recommendedRole) {
      setRoleMode(ROLE_MODE_RECOMMENDED);
      setSelectedRecommendedRole(recommendedRole);
      setCustomRoleName("");
    } else {
      setRoleMode(ROLE_MODE_CUSTOM);
      setSelectedRecommendedRole(null);
      setCustomRoleName(role.name);
    }

    setRoleDescription(role.description || "");
    setSelectedPermissions(role.permissions.map((p) => p.id));
    setPermissionSource("manual");
    setPendingTemplateRole(null);
    fetchPermissions();
    setDrawerOpen(true);
    setErrors({});
    setSelectAll(false);
  };

  const handleDeleteClick = (role) => {
    setDeleteTarget(role);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    const role_id = deleteTarget.id;

    try {
      setLoading(true);
      const res = await axios.delete(`${API_DELETE_ROLE}/${role_id}`);
      if (res.data?.status_code === 200) {
        toast.success("Role deleted successfully");
        fetchRoles();
      } else {
        toast.error(res.data?.message || "Failed to delete role.");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error("Something went wrong while deleting.");
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    if (roleMode === ROLE_MODE_RECOMMENDED) return;

    setPermissionSource("manual");
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSelectAllPermissions = (checked) => {
    setPermissionSource("manual");
    setSelectAll(checked);
    setSelectedPermissions(checked ? permissionsList.map((perm) => perm.id) : []);
  };

  const handleRoleModeChange = (_, nextMode) => {
    if (!nextMode) return;

    setRoleMode(nextMode);
    setErrors((prev) => ({ ...prev, roleMode: undefined, recommendedRole: undefined, customRoleName: undefined }));

    if (nextMode === ROLE_MODE_CUSTOM) {
      setSelectedRecommendedRole(null);
      setCustomRoleName("");
      setRoleDescription("");
      setSelectedPermissions([]);
      setSelectAll(false);
      setPermissionSource(null);
      setPendingTemplateRole(null);
      return;
    }

    setCustomRoleName("");
    setSelectedRecommendedRole(null);
    setSelectedPermissions([]);
    setRoleDescription("");
    setSelectAll(false);
    setPermissionSource(null);
    setPendingTemplateRole(null);
  };

  const checkIfRoleExists = useCallback(
    async (roleName) => {
      const normalized = roleName.trim().toLowerCase();
      const existsLocally = roles.some(
        (role) => role.name?.trim().toLowerCase() === normalized
      );

      if (existsLocally) return true;

      try {
        const res = await axios.get(API_GET_ROLE_BY_NAME, {
          params: { name: roleName, user_id },
        });
        return (res.data?.roles || []).some(
          (role) =>
            role.user_id === user_id &&
            role.role_name?.trim().toLowerCase() === normalized
        );
      } catch (err) {
        console.error("Error checking role existence:", err);
        return false;
      }
    },
    [roles, user_id]
  );

  const handleSaveRole = async () => {
    if (editId && roleMode === ROLE_MODE_RECOMMENDED) {
      toast.info("Recommended roles cannot be updated.");
      return;
    }

    const newErrors = {};

    if (roleMode === ROLE_MODE_RECOMMENDED && !selectedRecommendedRole) {
      newErrors.recommendedRole = "Please select a recommended role template";
      toast.error("Please select a recommended role");
    }

    if (roleMode === ROLE_MODE_CUSTOM && !customRoleName.trim()) {
      newErrors.customRoleName = "Enter a custom role name";
      toast.error("Custom role name is required");
    }

    if (selectedPermissions.length === 0) {
      newErrors.permissions = "Select at least one permission";
      toast.error("Select at least one permission");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const finalRoleName =
      roleMode === ROLE_MODE_RECOMMENDED
        ? selectedRecommendedRole.name
        : customRoleName.trim();

    if (!editId) {
      const roleExists = await checkIfRoleExists(finalRoleName);
      if (roleExists) {
        setRoleExistsDialog({ roleName: finalRoleName });
        return;
      }
    }

    const permissions = permissionsList
      .filter((perm) => selectedPermissions.includes(perm.id))
      .map((perm) => ({
        id: perm.id,
        permission_id: perm.permission_id,
        name: perm.name,
      }));

    const body = {
      user_id,
      name: finalRoleName,
      description: roleDescription,
      permissions,
    };

    try {
      setLoading(true);
      const response = editId
        ? await axios.put(`${API_UPDATES_ROLE}/${editId}`, body)
        : await axios.post(API_ADD_ROLES, body);

      const respData = response.data;
      if (respData.status_code === 200) {
        fetchRoles();
        setDrawerOpen(false);
      } else {
        const message = String(respData?.message || "").toLowerCase();
        if (!editId && message.includes("exist")) {
          setRoleExistsDialog({ roleName: finalRoleName });
        } else {
          toast.error(respData?.message);
        }
      }
    } catch (err) {
      console.error("Failed to save role:", err);
      toast.error("Failed to save role");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const fetchPermissionCatalog = async () => {
      try {
        const res = await axios.get(API_GET_ALL_PERMISSIONS);
        setAllPermissionsCatalog(res.data.permissions || []);
      } catch (err) {
        console.error("Error fetching permission catalog:", err);
      }
    };

    fetchPermissionCatalog();
  }, []);

  const groupPermissionsByCategory = useCallback(
    (permissions = []) => {
      const lookup = new Map();
      allPermissionsCatalog.forEach((perm) => {
        lookup.set(String(perm.id), perm);
        if (perm.permission_id) lookup.set(perm.permission_id, perm);
      });

      const grouped = {};
      const categoryOrder = [];

      permissions.forEach((perm) => {
        const meta =
          lookup.get(String(perm.id)) ||
          lookup.get(perm.permission_id) ||
          perm;
        const category = meta.category || perm.category || "Other";

        if (!grouped[category]) {
          grouped[category] = [];
          categoryOrder.push(category);
        }

        grouped[category].push({
          ...perm,
          name: perm.name || meta.name,
          description: meta.description || perm.description,
          category,
        });
      });

      return { grouped, categoryOrder };
    },
    [allPermissionsCatalog]
  );

  const previewGroupedPermissions = useMemo(() => {
    if (!permissionsPreview?.permissions?.length) {
      return { grouped: {}, categoryOrder: [] };
    }
    return groupPermissionsByCategory(permissionsPreview.permissions);
  }, [permissionsPreview, groupPermissionsByCategory]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [drawerOpen]);

  useEffect(() => {
    if (!editId && permissionsList.length > 0) {
      const allSelected = permissionsList.every((perm) =>
        selectedPermissions.includes(perm.id)
      );
      setSelectAll(allSelected);
    }
  }, [selectedPermissions, permissionsList, editId]);

  useEffect(() => {
    if (pendingTemplateRole && permissionsList.length > 0) {
      applyTemplatePermissions(pendingTemplateRole);
    }
  }, [pendingTemplateRole, permissionsList, applyTemplatePermissions]);

  useEffect(() => {
    if (drawerOpen && !editId) {
      loadDefaultRoleCounts();
    }
  }, [drawerOpen, editId, permissionsList, loadDefaultRoleCounts]);

  const groupedPermissions = useMemo(
    () =>
      permissionsList.reduce((acc, perm) => {
        const category = perm.category || "Uncategorized";
        if (!acc[category]) acc[category] = [];
        acc[category].push(perm);
        return acc;
      }, {}),
    [permissionsList]
  );

  const roleStats = useMemo(
    () => ({
      total: roles.length,
      recommended: roles.filter((role) => isRecommendedRoleName(role.name)).length,
      custom: roles.filter((role) => !isRecommendedRoleName(role.name)).length,
    }),
    [roles]
  );

  const filteredRoles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return roles;

    return roles.filter(
      (role) =>
        role.name?.toLowerCase().includes(query) ||
        role.description?.toLowerCase().includes(query) ||
        role.permissions?.some((perm) => perm.name?.toLowerCase().includes(query))
    );
  }, [roles, searchQuery]);

  const renderPermissionChips = useCallback((permissions = [], roleName = "") => {
    if (!permissions.length) {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No permissions assigned
        </Typography>
      );
    }

    const visible = permissions.slice(0, MAX_VISIBLE_PERMISSIONS);
    const hiddenCount = permissions.length - visible.length;

    return (
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ py: 0.5, width: "100%" }}>
        {visible.map((perm) => (
          <Chip
            key={perm.id || perm.permission_id || perm.name}
            label={perm.name}
            size="small"
            sx={{
              height: 24,
              ...roleTypography.chip,
              fontWeight: 500,
              backgroundColor: VM_Colors.white1,
              border: `1px solid ${VM_Colors.light_gray}`,
            }}
          />
        ))}
        {hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount} more`}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setPermissionsPreview({ roleName, permissions });
            }}
            sx={{
              height: 24,
              ...roleTypography.chip,
              fontWeight: 700,
              cursor: "pointer",
              backgroundColor: `${VM_Colors.primary}10`,
              color: VM_Colors.primary,
              "&:hover": {
                backgroundColor: `${VM_Colors.primary}20`,
              },
            }}
          />
        )}
      </Stack>
    );
  }, []);

  const columns = [
    renderSNo(),
    {
      field: "name",
      headerName: "Role Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const isRecommended = isRecommendedRoleName(params.value);
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", py: 0.5 }}>
            {/* <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: isRecommended ? `${VM_Colors.primary}14` : `${VM_Colors.secondary}33`,
                color: VM_Colors.primary,
              }}
            >
              {(params.value || "?").charAt(0).toUpperCase()}
            </Avatar> */}
            <Box sx={{ minWidth: 0 }}>
              <Tooltip title={params.value || "-"}>
                <Typography sx={roleTypography.bodySemibold} noWrap>
                  {params.value || "-"}
                </Typography>
              </Tooltip>
              {/* <Chip
                size="small"
                label={isRecommended ? "Recommended" : "Custom"}
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor: isRecommended
                    ? `${VM_Colors.primary}12`
                    : `${VM_Colors.secondary}22`,
                  color: VM_Colors.primary,
                }}
              /> */}
            </Box>
          </Stack>
        );
      },
    },
    // {
    //   field: "description",
    //   headerName: "Description",
    //   flex: 1.5,
    //   minWidth: 160,
    //   renderCell: (params) => (
    //     <Tooltip title={params.value || "No description"}>
    //       <Typography
    //         variant="body2"
    //         color={params.value ? "text.primary" : "text.secondary"}
    //         sx={{
    //           ...align_center,
    //           display: "-webkit-box",
    //           WebkitLineClamp: 2,
    //           WebkitBoxOrient: "vertical",
    //           overflow: "hidden",
    //           fontStyle: params.value ? "normal" : "italic",
    //         }}
    //       >
    //         {params.value || "No description"}
    //       </Typography>
    //     </Tooltip>
    //   ),
    // },
    {
      field: "permissions",
      headerName: "Permissions",
      flex: 2,
      minWidth: 220,
      renderCell: (params) => renderPermissionChips(params.value, params.row.name),
    },
    {
      field: "permissionCount",
      headerName: "Count",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.permissions?.length || 0}
          size="small"
          sx={{
            minWidth: 36,
            fontWeight: 700,
            backgroundColor: VM_Colors.white1,
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit role">
            <span>
            <IconButton
              onClick={() => handleEditClick(params.row)}
              size="small"
              sx={{
                border: `1px solid ${VM_Colors.light_gray}`,
                borderRadius: 1.5,
                color: VM_Colors.primary,
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete role">
            <span>
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              size="small"
              sx={{
                border: `1px solid ${VM_Colors.light_gray}`,
                borderRadius: 1.5,
                color: VM_Colors.error,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const renderStatCard = (icon, label, value, accent) => (
    <Grid item xs={12} sm={4} key={label}>
      <Paper elevation={0} sx={statCardSx}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${accent}14`,
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );

  const renderRolesList = () => (
    <Stack spacing={2.5}>
      {/* <Grid container spacing={2}>
        {renderStatCard(
          <GroupsOutlinedIcon />,
          "Total Roles",
          roleStats.total,
          VM_Colors.primary
        )}
        {renderStatCard(
          <AutoAwesomeIcon />,
          "Recommended",
          roleStats.recommended,
          VM_Colors.teal
        )}
        {renderStatCard(
          <BadgeOutlinedIcon />,
          "Custom Roles",
          roleStats.custom,
          VM_Colors.lead
        )}
      </Grid> */}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${VM_Colors.light_gray}`,
          backgroundColor: VM_Colors.white,
        }}
      >
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          spacing={2}
          justifyContent="flex-end"
          alignItems={isSmallScreen ? "stretch" : "center"}
          sx={{ mb: 2 }}
        >
          {/* <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Role directory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage access levels and permission sets for your team.
            </Typography>
          </Box> */}
          <TextField
            size="small"
            placeholder="Search ..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: isSmallScreen ? "100%" : 320,
              backgroundColor: VM_Colors.white,
            }}
          />
        </Stack>

        <ReusableDataGrid
          rows={filteredRoles}
          columns={columns}
          page={page}
          pageSize={pageSize}
          rowCount={filteredRoles.length}
          paginationMode="client"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 25]}
          autoHeight
          variableRowHeight
          loading={loading}
          customSx={{
            ...dataGridStyle,
            border: "none",
            fontFamily: ROLE_FONT_FAMILY,
            "& .MuiDataGrid-cell": {
              ...dataGridStyle["& .MuiDataGrid-cell"],
              fontFamily: ROLE_FONT_FAMILY,
              fontSize: 14,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontFamily: ROLE_FONT_FAMILY,
              fontSize: 14,
              fontWeight: 600,
            },
            "& .MuiDataGrid-columnHeaders": {
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            },
          }}
        />
      </Paper>
    </Stack>
  );

  const renderRoleModeOption = ({ mode, Icon, title, caption, palette }) => {
    const isSelected = roleMode === mode;

    return (
      <Card
        key={mode}
        elevation={0}
        onClick={() => handleRoleModeChange(null, mode)}
        sx={{
          flex: 1,
          cursor: "pointer",
          borderRadius: 2.5,
          border: `2px solid ${isSelected ? palette.accent : palette.border}`,
          backgroundColor: isSelected ? palette.selectedBg : palette.bg,
          color: isSelected ? VM_Colors.white : palette.textColor,
          boxShadow: isSelected ? palette.selectedShadow : "none",
          transition: "all 0.22s ease",
          position: "relative",
          "&:hover": {
            backgroundColor: isSelected ? palette.selectedHoverBg : palette.bgHover,
            borderColor: palette.accent,
            transform: "translateY(-1px)",
            boxShadow: isSelected
              ? palette.selectedShadow
              : `0 4px 16px ${palette.hoverShadow}`,
          },
        }}
      >
        <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
          {isSelected && (
            <CheckCircleIcon
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                fontSize: 22,
                color: VM_Colors.white,
              }}
            />
          )}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                backgroundColor: isSelected
                  ? "rgba(255,255,255,0.18)"
                  : palette.iconBg,
                color: isSelected ? VM_Colors.white : palette.iconColor,
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ pr: isSelected ? 2.5 : 0 }}>
              <Typography sx={roleTypography.cardTitle}>{title}</Typography>
              <Typography
                sx={{
                  ...roleTypography.caption,
                  display: "block",
                  mt: 0.35,
                  color: isSelected ? "rgba(255,255,255,0.88)" : "text.secondary",
                }}
              >
                {caption}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderRecommendedRoleCard = (recommendedRole) => {
    const IconComponent = RECOMMENDED_ROLE_ICONS[recommendedRole.name] || SupervisorAccountIcon;
    const isSelected = selectedRecommendedRole?.id === recommendedRole.id;
    const permissionCount =
      defaultRoleCounts[recommendedRole.id] ??
      (permissionsList.length
        ? countTemplatePermissions(permissionsList, recommendedRole.template)
        : 0);

    return (
      <Grid item xs={12} sm={6} md={3} key={recommendedRole.id}>
        <Card
          variant="outlined"
          onClick={() => applyRecommendedRole(recommendedRole)}
          sx={{
            height: "100%",
            cursor: "pointer",
            borderRadius: 2.5,
            borderWidth: isSelected ? 2 : 1,
            borderColor: isSelected ? VM_Colors.primary : VM_Colors.light_gray,
            backgroundColor: isSelected ? `${VM_Colors.primary}0A` : VM_Colors.white,
            boxShadow: isSelected ? `0 6px 20px ${VM_Colors.primary}20` : "none",
            transition: "all 0.22s ease",
            position: "relative",
            overflow: "hidden",
            "&::before": isSelected
              ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 4,
                  height: "100%",
                  backgroundColor: VM_Colors.primary,
                }
              : undefined,
            "&:hover": {
              borderColor: VM_Colors.primary,
              boxShadow: `0 8px 24px ${VM_Colors.primary}22`,
              transform: "translateY(-2px)",
            },
          }}
        >
          <CardContent sx={{ p: 2.5, pl: isSelected ? 2.75 : 2.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: `${VM_Colors.primary}18`,
                    color: VM_Colors.primary,
                  }}
                >
                  <IconComponent fontSize="small" />
                </Box>
                {isSelected && (
                  <CheckCircleIcon sx={{ fontSize: 20, color: VM_Colors.primary }} />
                )}
              </Stack>

              <Box>
                <Typography sx={roleTypography.cardTitle}>
                  {recommendedRole.name}
                </Typography>
                <Typography
                  sx={{
                    ...roleTypography.caption,
                    color: "text.secondary",
                    fontWeight: 600,
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  {recommendedRole.tagline}
                </Typography>
              </Box>

              <Chip
                size="small"
                label={`${permissionCount} permissions`}
                sx={{
                  alignSelf: "flex-start",
                  ...roleTypography.chip,
                  fontWeight: 600,
                  backgroundColor: `${VM_Colors.primary}14`,
                  color: VM_Colors.primary,
                  borderRadius: 1.5,
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderAddRole = () => {
    const isRecommendedEdit = Boolean(editId) && roleMode === ROLE_MODE_RECOMMENDED;
    const showCustomRoleName = roleMode === ROLE_MODE_CUSTOM;

    const roleNameField = (
      <StyledInputField
        heading={editId ? "Role Name" : "Custom Role Name"}
        required
        fullWidth
        size="small"
        disabled={isRecommendedEdit}
        value={isRecommendedEdit ? selectedRecommendedRole?.name || "" : customRoleName}
        onChange={(e) => {
          setCustomRoleName(e.target.value);
          setErrors((prev) => ({ ...prev, customRoleName: undefined }));
        }}
        error={!!errors.customRoleName}
        helperText={errors.customRoleName}
        placeholder={!editId ? "e.g. Accounts Manager, Event Coordinator" : undefined}
        sx={compactRoleFieldSx}
      />
    );

    return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${VM_Colors.light_gray}`,
        backgroundColor: VM_Colors.white,
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        fontFamily: ROLE_FONT_FAMILY,
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 } }}>
        <TitleAndCloseComponent
          title={editId ? "Update Role" : "Add New Role"}
          onClick={() => setDrawerOpen(false)}
          bottomBorderLine={false}
          titleAlign="left"
          sx={{ py: 1.25, my: 0 }}
        />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5 }}>
        {!editId && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ ...roleTypography.bodySemibold, mb: 1, fontSize: 14 }}>
              Role Setup
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {renderRoleModeOption({
                mode: ROLE_MODE_RECOMMENDED,
                Icon: AutoAwesomeIcon,
                title: "Recommended Roles",
                caption: "Start with a proven permission set for common venue team roles.",
                palette: roleSelectionPalette,
              })}
              {renderRoleModeOption({
                mode: ROLE_MODE_CUSTOM,
                Icon: TuneIcon,
                title: "Custom Role",
                caption: "Name the role and choose permissions yourself.",
                palette: roleSelectionPalette,
              })}
            </Stack>
            {errors.roleMode && (
              <Typography sx={{ ...roleTypography.caption, color: "error.main", mt: 1 }}>
                {errors.roleMode}
              </Typography>
            )}
          </Box>
        )}

        {!editId && roleMode === ROLE_MODE_RECOMMENDED && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ ...roleTypography.bodySemibold, fontSize: 14 }}>
              Choose a recommended role
            </Typography>
            <Typography sx={{ ...roleTypography.caption, color: "text.secondary", mb: 1.5, display: "block" }}>
              Start with a proven permission set for common venue team roles. Permissions
              follow the selected template.
            </Typography>
            <Grid container spacing={1.5}>
              {RECOMMENDED_ROLES.map((recommendedRole) =>
                renderRecommendedRoleCard(recommendedRole)
              )}
            </Grid>
            {errors.recommendedRole && (
              <Typography sx={{ ...roleTypography.caption, color: "error.main", mt: 1 }}>
                {errors.recommendedRole}
              </Typography>
            )}
          </Box>
        )}

        {(showCustomRoleName || isRecommendedEdit) && (
          <Box sx={{ mb: 2.5 }}>{roleNameField}</Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <StyledInputField
            heading="Description"
            fullWidth
            multiline
            rows={2}
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            placeholder="Optional description for this role"
            sx={compactRoleFieldSx}
          />
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            border: `1px solid ${VM_Colors.light_gray}`,
            overflow: "hidden",
            backgroundColor: VM_Colors.white,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{
              px: 2,
              py: 1.25,
              backgroundColor: "#F8FAFC",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AdminPanelSettingsOutlinedIcon sx={{ color: VM_Colors.primary, fontSize: 18 }} />
              <MuiTypographyMandatory
                label="Permissions"
                required
                sx={{
                  ...roleTypography.bodySemibold,
                  color: VM_Colors.primary,
                  fontSize: 14,
                }}
              />
            </Stack>
            <Chip
              size="small"
              label={`${selectedPermissions.length} selected`}
              sx={{
                ...roleTypography.chip,
                height: 24,
                fontWeight: 600,
                backgroundColor: VM_Colors.white,
                border: `1px solid ${VM_Colors.light_gray}`,
              }}
            />
          </Stack>

          {roleMode === ROLE_MODE_CUSTOM && (
            <Box sx={{ px: 2, py: 0.5, backgroundColor: VM_Colors.white }}>
              <FormControlLabel
                sx={{ ml: -0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectAll}
                    onChange={(e) => handleSelectAllPermissions(e.target.checked)}
                    sx={{ color: VM_Colors.primary, "&.Mui-checked": { color: VM_Colors.primary } }}
                  />
                }
                label={<Typography sx={{ ...roleTypography.bodySemibold, fontSize: 13 }}>Select all permissions</Typography>}
              />
            </Box>
          )}

          {roleMode === ROLE_MODE_RECOMMENDED && (
            <Typography sx={{ ...roleTypography.caption, color: "text.secondary", px: 2, py: 1 }}>
              Permissions are locked for recommended roles and reflect the selected template.
            </Typography>
          )}

          {permissionSource === "saved" && (
            <Alert severity="info" sx={{ mx: 2, mb: 1.5, mt: 0.5, py: 0, borderRadius: 1.5 }}>
              {roleMode === ROLE_MODE_RECOMMENDED
                ? "Loaded your saved role configuration. Permissions are read-only for recommended roles."
                : "Loaded your saved role configuration. Adjust permissions if needed."}
            </Alert>
          )}

          {permissionSource === "default" && (
            <Alert severity="success" sx={{ mx: 2, mb: 1.5, mt: 0.5, py: 0, borderRadius: 1.5 }}>
              {roleMode === ROLE_MODE_RECOMMENDED
                ? "Applied system-recommended default permissions. These permissions are fixed for this role."
                : "Applied system-recommended default permissions. Review and customize before saving."}
            </Alert>
          )}

          {permissionSource === "template" && (
            <Alert severity="warning" sx={{ mx: 2, mb: 1.5, mt: 0.5, py: 0, borderRadius: 1.5 }}>
              {roleMode === ROLE_MODE_RECOMMENDED
                ? "Default role API unavailable. Applied a local fallback template — permissions are read-only."
                : "Default role API unavailable. Applied a local fallback template — review before saving."}
            </Alert>
          )}

          {errors.permissions && (
            <Typography sx={{ ...roleTypography.caption, color: "error.main", px: 2, pb: 1, display: "block" }}>
              {errors.permissions}
            </Typography>
          )}

          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            {Object.entries(groupedPermissions).map(([category, perms]) => {
              const selectedInCategory = perms.filter((perm) =>
                selectedPermissions.includes(perm.id)
              ).length;

              return (
              <Box key={category} sx={{ mb: 2.5, "&:last-child": { mb: 0 } }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography sx={{ ...roleTypography.bodySemibold, fontSize: 13, color: VM_Colors.primary }}>
                    {category}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedInCategory}/${perms.length}`}
                    sx={{
                      ...roleTypography.chip,
                      height: 22,
                      fontWeight: 600,
                      backgroundColor: selectedInCategory ? `${VM_Colors.primary}14` : VM_Colors.white1,
                    }}
                  />
                </Stack>
                <Grid container spacing={1.25}>
                  {perms.map((perm) => {
                    const isChecked = selectedPermissions.includes(perm.id);
                    const isPermissionsReadOnly = roleMode === ROLE_MODE_RECOMMENDED;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={perm.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 1.5,
                            minWidth: 0,
                            height: "100%",
                            borderColor: isChecked ? VM_Colors.primary : VM_Colors.light_gray,
                            backgroundColor: isChecked ? `${VM_Colors.primary}08` : VM_Colors.white,
                            opacity: isPermissionsReadOnly && !isChecked ? 0.72 : 1,
                            boxShadow: "none",
                          }}
                        >
                          <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                            <FormControlLabel
                              sx={{
                                alignItems: "flex-start",
                                m: 0,
                                width: "100%",
                                cursor: isPermissionsReadOnly ? "default" : "pointer",
                              }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isChecked}
                                  disabled={isPermissionsReadOnly}
                                  onChange={() => handlePermissionToggle(perm.id)}
                                  sx={{
                                    pt: 0.15,
                                    color: VM_Colors.primary,
                                    "&.Mui-checked": { color: VM_Colors.primary },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography sx={{ ...roleTypography.bodySemibold, fontSize: 13 }}>
                                    {perm.name}
                                  </Typography>
                                  {perm.description && (
                                    <Typography sx={{ ...roleTypography.caption, color: "text.secondary" }}>
                                      {perm.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              );
            })}
          </Box>
        </Box>

        <Stack
          direction={isSmallScreen ? "column" : "row"}
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 2.5 }}
        >
          <Button
            variant="outlined"
            onClick={() => setDrawerOpen(false)}
            disabled={isRecommendedEdit}
            sx={{ ...secondaryActionButtonSx, flex: isSmallScreen ? 1 : "none", minWidth: 120 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveRole}
            disabled={isRecommendedEdit}
            sx={{ ...primaryActionButtonSx, flex: isSmallScreen ? 1 : "none", minWidth: 140 }}
          >
            {editId ? "Update Role" : "Save Role"}
          </Button>
        </Stack>
      </Box>
    </Paper>
    );
  };

  return (
    <Box sx={{ ...roleModuleRootSx, p: { xs: 2, md: 3 }, minHeight: "100vh" }}>
      {!drawerOpen ? (
        <>
          <TitleWithAddButton
            title="Roles & Permissions"
            buttonLabel="Add Role"
            onAddClick={handleAddRoleClick}
          />
          {renderRolesList()}
        </>
      ) : (
        <>
          {/* <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <AdminPanelSettingsOutlinedIcon sx={{ color: VM_Colors.primary, fontSize: 28 }} />
            <Box>
              <Typography sx={roleTypography.pageTitle}>
                {editId ? "Edit Role" : "Create Role"}
              </Typography>
              <Typography sx={{ ...roleTypography.body, color: "text.secondary" }}>
                Define role details and assign permissions for your team members.
              </Typography>
            </Box>
          </Stack> */}
          {renderAddRole()}
        </>
      )}

      {loading && <Loader loading={loading} />}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        aria-labelledby="delete-role-dialog-title"
      >
        <DialogTitle id="delete-role-dialog-title" sx={roleTypography.sectionTitle}>
          Delete role
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{deleteTarget?.name}</strong>? Team members assigned to this role may
            lose access.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteTarget(null)}
            sx={{ ...secondaryActionButtonSx, flex: "none", minWidth: 100, px: 3, py: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(permissionsPreview)}
        onClose={() => setPermissionsPreview(null)}
        aria-labelledby="role-permissions-dialog-title"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" },
        }}
      >
        <DialogTitle
          id="role-permissions-dialog-title"
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: VM_Colors.white1,
            borderBottom: `1px solid ${VM_Colors.light_gray}`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" >
            <Box>
              <Typography sx={{ ...roleTypography.sectionTitle, fontSize: 18 }}>
                Permissions for {permissionsPreview?.roleName}
              </Typography>
              {/* <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={`${permissionsPreview?.permissions?.length || 0} permissions`}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: `${VM_Colors.primary}12`,
                    color: VM_Colors.primary,
                  }}
                />
                <Chip
                  size="small"
                  label={`${previewGroupedPermissions.categoryOrder.length} categories`}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: VM_Colors.white,
                    border: `1px solid ${VM_Colors.light_gray}`,
                  }}
                />
              </Stack> */}
            </Box>
            <IconButton
              aria-label="Close permissions dialog"
              onClick={() => setPermissionsPreview(null)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5, }}>
          <Stack spacing={3} sx={{p:3}}>
            {previewGroupedPermissions.categoryOrder.map((category) => (
              <Box key={category}>
                <Typography
                  sx={{
                    ...roleTypography.sectionTitle,
                    mb: 1,
                    pb: 0.75,
                    borderBottom: `1px solid ${VM_Colors.light_gray}`,
                  }}
                >
                  {category}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {previewGroupedPermissions.grouped[category]?.map((perm) => (
                    <Chip
                      key={perm.id || perm.permission_id || perm.name}
                      label={perm.name}
                      size="small"
                      sx={{
                        height: 26,
                        ...roleTypography.chip,
                        fontWeight: 500,
                        backgroundColor: VM_Colors.white1,
                        border: `1px solid ${VM_Colors.light_gray}`,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${VM_Colors.light_gray}`,
            backgroundColor: VM_Colors.white1,
          }}
        >
          <Button
            onClick={() => setPermissionsPreview(null)}
            variant="contained"
            sx={{ ...primaryActionButtonSx, minWidth: 120, px: 3, py: 1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(roleExistsDialog)}
        onClose={() => setRoleExistsDialog(null)}
        aria-labelledby="role-exists-dialog-title"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" },
        }}
      >
        <DialogTitle
          id="role-exists-dialog-title"
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: VM_Colors.white1,
            borderBottom: `1px solid ${VM_Colors.light_gray}`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${VM_Colors.primary}18`,
                  color: VM_Colors.primary,
                }}
              >
                <InfoOutlinedIcon />
              </Box>
              <Typography sx={{ ...roleTypography.sectionTitle, color: "text.primary" }}>
                Alert
              </Typography>
            </Stack>
            <IconButton
              aria-label="Close dialog"
              onClick={() => setRoleExistsDialog(null)}
              size="small"
              sx={{ mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography sx={{ ...roleTypography.body, color: "text.secondary" }} lineHeight={1.6}>
            A role named{" "}
            <Typography component="span" sx={{ ...roleTypography.bodySemibold, color: "text.primary" }}>
              {roleExistsDialog?.roleName || "this name"}
            </Typography>{" "}
            already exists. Please choose a different name or edit the existing role from the
            list.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${VM_Colors.light_gray}`,
            backgroundColor: VM_Colors.white1,
          }}
        >
          <Button
            onClick={() => setRoleExistsDialog(null)}
            variant="contained"
            sx={{ ...primaryActionButtonSx, minWidth: 100, px: 3, py: 1 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VMRoles;